import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getISOWeek, type SelectedItem } from "@/lib/selected-weeks";
import { fetchPickedWeeks, fetchPicksForWeek } from "@/lib/selected-week-picks";
import { SmartImg } from "@/components/SmartImg";

export const Route = createFileRoute("/selected/archive")({
  head: () => ({
    meta: [
      { title: "Archiv, Selected, OM Selected" },
      { name: "description", content: "Archiv vergangener Kalenderwochen aus dem Selected-Bereich." },
      { property: "og:title", content: "Archiv, Selected" },
      { property: "og:description", content: "Vergangene Kalenderwochen aus dem Selected-Bereich." },
      { property: "og:url", content: "https://omselected.art/selected/archive" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/selected/archive" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Archiv, Selected, OM Selected",
          description: "Archiv vergangener Kalenderwochen aus dem Selected-Bereich.",
          url: "https://omselected.art/selected/archive",
          isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
        }),
      },
    ],
  }),
  component: ArchivePage,
});

function ArchivePage() {
  const { t } = useTranslation();
  const [archive, setArchive] = useState<{ year: number; week: number; items: SelectedItem[] }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const now = getISOWeek(new Date());
      const all = await fetchPickedWeeks();
      // The Selected page shows the current week, or if empty the most recent
      // week with picks. Exclude whichever week is featured there.
      const hasCurrent = all.some((w) => w.year === now.year && w.week === now.week);
      const featured = hasCurrent ? now : all[0];
      const past = featured
        ? all.filter((w) => !(w.year === featured.year && w.week === featured.week))
        : all;
      const weeks = await Promise.all(
        past.map(async (w) => ({ year: w.year, week: w.week, items: await fetchPicksForWeek(w.year, w.week) }))
      );
      if (!active) return;
      setArchive(weeks);
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">
            <Link to="/selected" className="hover:text-foreground">Selected</Link> · {t("archive.breadcrumb")}
          </div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">
            {t("archive.titlePre")}<br /><em className="italic">{t("archive.titleEm")}</em> {t("archive.titlePost")}
          </h1>
          <p className="mt-8 max-w-xl text-muted-foreground">
            {t("archive.intro")}
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
          {loading ? (
            <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
          ) : archive.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("archive.empty")}</p>
          ) : (
            <div className="space-y-16">
              {archive.map((aw) => (
                <div key={`${aw.year}-${aw.week}`}>
                  <div className="flex items-baseline justify-between mb-6 border-b border-border pb-3">
                    <h2 className="serif text-2xl md:text-3xl">{t("archive.week", { week: aw.week })}</h2>
                    <div className="eyebrow text-muted-foreground">{aw.year}</div>
                  </div>
                  {aw.items.length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t("archive.noImages")}</p>
                  ) : (
                    <div className="grid md:grid-cols-3 gap-6">
                      {aw.items.map((w) => (
                        <Link key={w.id} to="/photographers/$id" params={{ id: w.photographerId }} className="block group">
                          <SmartImg
                            source={w.image}
                            transform={{ width: 600, resize: "contain" }}
                            srcSetWidths={[400, 600, 900]}
                            sizes="(min-width: 768px) 33vw, 100vw"
                            alt={w.caption}
                            loading="lazy"
                            decoding="async"
                            width={600}
                            height={750}
                            className="w-full aspect-[4/5] object-contain bg-muted"
                          />
                          <div className="mt-3">
                            <div className="serif text-base">{w.caption}</div>
                            <div className="eyebrow text-muted-foreground mt-1">{w.photographerName}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}